/**
 * Thin DataDog Browser RUM wrapper: cached dynamic import, one-shot init + user/global context, read access.
 * No app/domain imports — orchestration lives in `configureRum.ts`.
 */

import type { RumGlobal, RumInitConfiguration, User } from "@datadog/browser-rum";

/** User context for RUM; `id` is required (deprecated overload is `User` with optional `id`). */
export type DatadogRumUser = User & { id: string };

export type DatadogRumBootstrap = {
    initConfiguration: RumInitConfiguration;
    user: DatadogRumUser;
    globalContext?: Record<string, unknown>;
};

/** Promise to import the RUM SDK; cached to avoid multiple imports. */
let rumSdkImportPromise: Promise<typeof import("@datadog/browser-rum")> | null = null;

/** True only after `initializeDatadogRum` completed successfully. */
let rumClientInitialized = false;

function loadRumSdk(): Promise<typeof import("@datadog/browser-rum")> {
    rumSdkImportPromise ??= import("@datadog/browser-rum");
    return rumSdkImportPromise;
}

/**
 * Loads the SDK (once), calls `init`, `setUser`, and sets global context. Idempotent if RUM was already initialized.
 * Intended to be called only from `configureDatadogRumIfNeeded` in `configureRum.ts` after org policy allows RUM.
 */
export async function initializeDatadogRum(bootstrap: DatadogRumBootstrap): Promise<void> {
    const appId = bootstrap.initConfiguration.applicationId.trim();
    const clientToken = bootstrap.initConfiguration.clientToken.trim();
    if (!appId || !clientToken) {
        return;
    }

    const { datadogRum } = await loadRumSdk();
    if (datadogRum.getInitConfiguration() !== undefined) {
        rumClientInitialized = true;
        return;
    }

    datadogRum.init(bootstrap.initConfiguration);
    datadogRum.setUser(bootstrap.user);
    for (const [key, value] of Object.entries(bootstrap.globalContext ?? {})) {
        datadogRum.setGlobalContextProperty(key, value);
    }

    rumClientInitialized = true;
}

/**
 * Returns the RUM client when `initializeDatadogRum` completed successfully; otherwise
 * `undefined` without importing the SDK (unless init already ran earlier in the session).
 */
export async function getDatadogRum(): Promise<RumGlobal | undefined> {
    if (!rumClientInitialized) {
        return undefined;
    }

    const { datadogRum } = await loadRumSdk();
    return datadogRum;
}
